/* Client for the PEAR console (platform-admin) endpoints on the payments backend.
   The console is invite-only: platform identity lives in platformAdmins/{uid},
   written only by the backend. Authed calls attach the Firebase ID token; the
   accept flow (/invite/:token, /accept) is public (token-gated). */

import { auth } from '../firebase';

const BASE = (process.env.REACT_APP_PAYMENTS_URL || '').replace(/\/$/, '');

export function isConsoleApiConfigured(): boolean {
  return !!BASE;
}

export interface ConsoleAdmin {
  uid: string;
  email: string;
  invitedBy: string | null;
  grandfather: boolean;
  addedAt: number | null;
}

async function authed(path: string, method: string, body?: unknown): Promise<Response> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('not-signed-in');
  return fetch(`${BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
}

/** Am I a platform (console) admin? Used to gate the console UI post-login. */
export async function fetchIsPlatformAdmin(): Promise<boolean> {
  if (!BASE) return false;
  try {
    const res = await authed('/api/console/me', 'GET');
    const json = await res.json().catch(() => ({}));
    return res.ok && !!json.isPlatformAdmin;
  } catch {
    return false;
  }
}

export async function fetchConsoleAdmins(): Promise<{ ok: boolean; admins: ConsoleAdmin[]; error?: string }> {
  if (!BASE) return { ok: false, admins: [], error: 'The backend is not configured.' };
  try {
    const res = await authed('/api/console/admins', 'GET');
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, admins: [], error: json.error || 'Could not load admins.' };
    return { ok: true, admins: Array.isArray(json.admins) ? json.admins : [] };
  } catch {
    return { ok: false, admins: [], error: 'Network error — please try again.' };
  }
}

export async function inviteConsoleAdmin(email: string): Promise<{ ok: boolean; error?: string; alreadyAdmin?: boolean }> {
  if (!BASE) return { ok: false, error: 'The backend is not configured.' };
  try {
    const res = await authed('/api/console/invite', 'POST', { email });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: json.error || 'Could not send the invite.' };
    return { ok: true, alreadyAdmin: !!json.alreadyAdmin };
  } catch {
    return { ok: false, error: 'Network error — please try again.' };
  }
}

export async function revokeConsoleAdmin(uid: string): Promise<{ ok: boolean; error?: string }> {
  if (!BASE) return { ok: false, error: 'The backend is not configured.' };
  try {
    const res = await authed('/api/console/revoke', 'POST', { uid });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: json.error || 'Could not remove that admin.' };
    return { ok: true };
  } catch {
    return { ok: false, error: 'Network error — please try again.' };
  }
}

/* ---- public accept flow (no auth) ---- */

export async function fetchInvite(token: string): Promise<{ ok: boolean; email?: string }> {
  if (!BASE) return { ok: false };
  try {
    const res = await fetch(`${BASE}/api/console/invite/${encodeURIComponent(token)}`);
    const json = await res.json().catch(() => ({}));
    return { ok: !!json.ok, email: json.email };
  } catch {
    return { ok: false };
  }
}

export async function acceptInvite(token: string, password: string): Promise<{ ok: boolean; email?: string; error?: string }> {
  if (!BASE) return { ok: false, error: 'The backend is not configured.' };
  try {
    const res = await fetch(`${BASE}/api/console/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: json.error || 'Could not complete signup.' };
    return { ok: true, email: json.email };
  } catch {
    return { ok: false, error: 'Network error — please try again.' };
  }
}

/* ---- per-school Zana / Claude budget (platform admin only) ---- */

export interface ZanaBudget {
  ok: boolean;
  orgCode: string;
  capUsd: number;
  spentMonthUsd: number;
  spentTodayUsd: number;
  remainingUsd: number;
  dailyUsd: number;
  evenDailyUsd: number;
  mode: 'off' | 'tight' | 'balanced' | 'generous';
  overCap: boolean;
  overReason: string | null;
  usingDefault: boolean;
  calls: number;
  askCalls: number;
  monthId: string;
  defaultUsd: number;
  hardMaxUsd: number;
  storedCapUsd: number | null;
  clamped?: boolean;
  error?: string;
}

async function zanaBudgetRes(path: string, method: string, body?: unknown): Promise<ZanaBudget> {
  if (!BASE) return { ok: false, orgCode: '', capUsd: 15, spentMonthUsd: 0, spentTodayUsd: 0, remainingUsd: 0, dailyUsd: 0, evenDailyUsd: 0, mode: 'off', overCap: true, overReason: null, usingDefault: true, calls: 0, askCalls: 0, monthId: '', defaultUsd: 15, hardMaxUsd: 100, storedCapUsd: null, error: 'The backend is not configured.' };
  try {
    const res = await authed(path, method, body);
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) {
      return { ok: false, orgCode: '', capUsd: 15, spentMonthUsd: 0, spentTodayUsd: 0, remainingUsd: 0, dailyUsd: 0, evenDailyUsd: 0, mode: 'off', overCap: true, overReason: null, usingDefault: true, calls: 0, askCalls: 0, monthId: '', defaultUsd: 15, hardMaxUsd: 100, storedCapUsd: null, error: json.error || 'Could not load Zana budget.' };
    }
    return json as ZanaBudget;
  } catch {
    return { ok: false, orgCode: '', capUsd: 15, spentMonthUsd: 0, spentTodayUsd: 0, remainingUsd: 0, dailyUsd: 0, evenDailyUsd: 0, mode: 'off', overCap: true, overReason: null, usingDefault: true, calls: 0, askCalls: 0, monthId: '', defaultUsd: 15, hardMaxUsd: 100, storedCapUsd: null, error: 'Network error — please try again.' };
  }
}

export async function fetchZanaBudget(orgCode: string): Promise<ZanaBudget> {
  return zanaBudgetRes(`/api/console/orgs/${encodeURIComponent(orgCode)}/zana-budget`, 'GET');
}

export async function saveZanaBudget(orgCode: string, monthlyCapUsd: number): Promise<ZanaBudget> {
  return zanaBudgetRes(`/api/console/orgs/${encodeURIComponent(orgCode)}/zana-budget`, 'PUT', { monthlyCapUsd });
}

/* ── Platform-wide Zana controls (PEAR console) ────────────────────────────
 * Per-school caps stop any one school; these two answer the questions that
 * span all of them — what is Zana costing across every customer this month,
 * and is her deeper path actually reachable right now. */

export interface ZanaPlatformSpend {
  ok: boolean;
  monthId: string;
  totalUsd: number;
  totalKes: number;
  totalCalls: number;
  /** Tokens actually used — what the answer ceiling should be sized against. */
  tokensIn: number;
  tokensOut: number;
  tokensCacheRead: number;
  /** Blends both ceilings — kept for compatibility. Prefer the split below. */
  avgTokensOut: number;
  /* Quick answers are written against a 900-token ceiling, deep ones against
     1,400, so one average across both can be checked against neither. Null
     until a call has been recorded since the split shipped — "not measured
     yet" is a different statement from zero. */
  avgTokensQuick: number | null;
  avgTokensDeep: number | null;
  quickCalls: number;
  deepCalls: number;
  /** Answers that hit their ceiling and were retried with more room. */
  truncatedCalls: number;
  /* Questions answered, as distinct from every model call. The averages above
     cover these only — briefings, digests and takeover narration write against
     their own ceilings and are deliberately not pooled in. Shown so "34 calls"
     can never again sit beside "Model calls 448" with nothing to explain it. */
  askCalls: number;
  orgCount: number;
  truncated: boolean;
  orgs: { orgCode: string; usd: number; kes: number; calls: number; askCalls: number }[];
  health: { healthy: boolean; latchedOrgs: string[]; consecutiveFailures: number; pendingOrgs: number; reason: string | null };
  brainOn: boolean;
  error?: string;
}

const EMPTY_SPEND: ZanaPlatformSpend = {
  ok: false, monthId: '', totalUsd: 0, totalKes: 0, totalCalls: 0,
  tokensIn: 0, tokensOut: 0, tokensCacheRead: 0, avgTokensOut: 0,
  avgTokensQuick: null, avgTokensDeep: null, quickCalls: 0, deepCalls: 0,
  truncatedCalls: 0, askCalls: 0, orgCount: 0,
  truncated: false, orgs: [],
  health: { healthy: true, latchedOrgs: [], consecutiveFailures: 0, pendingOrgs: 0, reason: null },
  brainOn: false,
};

export async function fetchZanaPlatformSpend(): Promise<ZanaPlatformSpend> {
  if (!BASE) return { ...EMPTY_SPEND, error: 'The backend is not configured.' };
  try {
    const res = await authed('/api/console/zana/spend', 'GET');
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) return { ...EMPTY_SPEND, error: json.error || 'Could not read platform Zana spend.' };
    return json as ZanaPlatformSpend;
  } catch {
    return { ...EMPTY_SPEND, error: 'Network error — please try again.' };
  }
}

export interface ZanaKillSwitch {
  ok: boolean;
  off: boolean;
  updatedAt: number | null;
  updatedBy: string | null;
  error?: string;
}

async function killSwitchRes(method: string, body?: unknown): Promise<ZanaKillSwitch> {
  if (!BASE) return { ok: false, off: false, updatedAt: null, updatedBy: null, error: 'The backend is not configured.' };
  try {
    const res = await authed('/api/console/zana/kill-switch', method, body);
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) return { ok: false, off: false, updatedAt: null, updatedBy: null, error: json.error || 'Could not reach the kill switch.' };
    return json as ZanaKillSwitch;
  } catch {
    return { ok: false, off: false, updatedAt: null, updatedBy: null, error: 'Network error — please try again.' };
  }
}

export async function fetchZanaKillSwitch(): Promise<ZanaKillSwitch> {
  return killSwitchRes('GET');
}

/** `off: true` stops every paid Zana call for every school, immediately. */
export async function setZanaKillSwitch(off: boolean): Promise<ZanaKillSwitch> {
  return killSwitchRes('PUT', { off });
}

export interface EngineRunRow {
  id: string;
  engine?: string;
  orgCode?: string | null;
  durationMs?: number;
  findingCount?: number;
  ok?: boolean;
  detail?: string | null;
  at?: number;
}

export interface EngineRunsPayload {
  ok: boolean;
  runs: EngineRunRow[];
  byOrg?: Record<string, number>;
  summary?: Array<Record<string, unknown>>;
  orgFilter?: string | null;
  error?: string;
}

/** Recent mesh / organ job rows — LEAF + other orgs for platform drill. */
export async function fetchEngineRuns(orgCode?: string): Promise<EngineRunsPayload> {
  if (!BASE) return { ok: false, runs: [], error: 'The backend is not configured.' };
  try {
    const q = orgCode ? `?orgCode=${encodeURIComponent(orgCode)}` : '';
    const res = await authed(`/api/console/zana/engine-runs${q}`, 'GET');
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) {
      return { ok: false, runs: [], error: json.error || 'Could not read engine runs.' };
    }
    return json as EngineRunsPayload;
  } catch {
    return { ok: false, runs: [], error: 'Network error — please try again.' };
  }
}

/* ── Working hours (off-trip tracking) — the per-org switch ────────────────
   💸 The one policy that decides how often an UNCAPPED paid platform gets
   written to. Off-trip recording roughly DOUBLES GPS writes, and Blaze has no
   hard spend cap — so this had to stop being a constant compiled into the
   phone, where turning it OFF meant shipping a release to every driver.
   `enabled: null` means no override: the app's own default applies. */
export interface WorkingHoursSwitch {
  ok: boolean;
  orgCode?: string;
  enabled: boolean | null;
  updatedAt?: number | null;
  updatedBy?: string | null;
  error?: string;
}

async function workingHoursRes(
  method: string, orgCode: string, body?: unknown,
): Promise<WorkingHoursSwitch> {
  if (!BASE) return { ok: false, enabled: null, error: 'The backend is not configured.' };
  try {
    const path = method === 'GET'
      ? `/api/console/tracking/working-hours?orgCode=${encodeURIComponent(orgCode)}`
      : '/api/console/tracking/working-hours';
    const res = await authed(path, method, body);
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) {
      return { ok: false, enabled: null, error: json.error || 'Could not reach the working-hours switch.' };
    }
    return json as WorkingHoursSwitch;
  } catch {
    return { ok: false, enabled: null, error: 'Network error — please try again.' };
  }
}

export async function fetchWorkingHours(orgCode: string): Promise<WorkingHoursSwitch> {
  return workingHoursRes('GET', orgCode);
}

/** `enabled: null` clears the override and hands control back to the app default. */
export async function setWorkingHours(orgCode: string, enabled: boolean | null): Promise<WorkingHoursSwitch> {
  return workingHoursRes('PUT', orgCode, { orgCode, enabled });
}

export interface ManualEpraCeilingResult {
  ok: boolean;
  reason?: string;
  error?: string;
  caps?: { diesel: number; petrol: number };
  refreshedOrgCount?: number;
}

export interface RouteLearningResetResult {
  ok: boolean;
  profiles?: number;
  deleted?: number;
  error?: string;
}

/** PEAR console — forget an org's route learning and start again from the next trip. */
export async function resetRouteLearning(orgCode: string): Promise<RouteLearningResetResult> {
  if (!BASE) return { ok: false, error: 'The backend is not configured.' };
  try {
    const res = await authed('/api/console/route-learning/reset', 'POST', { orgCode });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) {
      return { ok: false, error: (json.error as string) || 'Could not reset route learning.' };
    }
    return json as RouteLearningResetResult;
  } catch {
    return { ok: false, error: 'Network error — please try again.' };
  }
}

/** PEAR console — why hasn't a route learned? Read-only readout (bug 129). */
export interface RouteLearningStatusProfile {
  id: string; busId: string; routeBucket: string; tripCount: number; isLearned: boolean;
  avgDistanceKm: number | null; avgDurationMinutes: number | null; startLabel: string; endLabel: string;
}
export interface RouteLearningStatusTrip {
  id: string; busId: string; routeType: string; completedAt: string;
  distanceKm: number | null; durationMinutes: number | null;
  sLat: number | null; sLng: number | null; eLat: number | null; eLng: number | null;
  hasRi: boolean; skip: string | null;
  /** Breadcrumbs stored on the trip doc, and where its distance came from. */
  points?: number; distanceKmSource?: string | null;
  /** Idle seconds the phone counted on this trip (null when never stamped). */
  idleSeconds?: number | null;
  /** Road-snap outcome: snapped km when it worked, the Google error when not. */
  snappedKm?: number | null;
  snapError?: string | null;
}
export interface RouteLearningStatusCluster {
  busId: string; bucket: string; sLat: number; sLng: number; eLat: number; eLng: number;
  trips: number; tripIds: string[]; wouldBeLearned: boolean;
}
export interface RouteLearningStatus {
  ok: boolean;
  error?: string;
  orgCode?: string;
  resetAt?: string | null;
  learnedAfterTrips?: number;
  anchorToleranceM?: number;
  profiles?: RouteLearningStatusProfile[];
  trips?: RouteLearningStatusTrip[];
  clusters?: RouteLearningStatusCluster[];
}

export async function routeLearningStatus(orgCode: string): Promise<RouteLearningStatus> {
  if (!BASE) return { ok: false, error: 'The backend is not configured.' };
  try {
    const res = await authed('/api/console/route-learning/status', 'POST', { orgCode });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) {
      return { ok: false, error: (json.error as string) || 'Could not read route learning.' };
    }
    return json as RouteLearningStatus;
  } catch {
    return { ok: false, error: 'Network error — please try again.' };
  }
}

/** PEAR console — wipe an org's fuel data (events, station stops, engine state,
 *  fuel alerts, cached bus fields) so testing starts clean. Config + prices kept. */
export interface FuelResetResult {
  ok: boolean;
  error?: string;
  deleted?: number;
  counts?: { refuelEvents: number; stationStops: number; fuelStates: number; baselines: number; engineAlerts: number; fleetAlerts: number; buses: number };
}
export async function resetFuelData(orgCode: string, dryRun = false): Promise<FuelResetResult> {
  if (!BASE) return { ok: false, error: 'The backend is not configured.' };
  try {
    const res = await authed('/api/console/fuel/reset', 'POST', { orgCode, dryRun });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) {
      return { ok: false, error: (json.error as string) || 'Could not reset fuel data.' };
    }
    return json as FuelResetResult;
  } catch {
    return { ok: false, error: 'Network error — please try again.' };
  }
}

/** PEAR console — rebuild one org's route profiles from the trip log right now
 *  (the 06:00 UTC reconcile, on demand). Used after a heal. */
export async function rebuildRouteLearning(orgCode: string): Promise<{ ok: boolean; error?: string; summary?: unknown }> {
  if (!BASE) return { ok: false, error: 'The backend is not configured.' };
  try {
    const res = await authed('/api/console/route-learning/rebuild', 'POST', { orgCode });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) {
      return { ok: false, error: (json.error as string) || 'Could not rebuild route learning.' };
    }
    return json as { ok: boolean; summary?: unknown };
  } catch {
    return { ok: false, error: 'Network error — please try again.' };
  }
}

/** PEAR console — override wrong press consensus with official EPRA Nairobi caps. */
export async function applyManualEpraCeiling(args: {
  diesel: number;
  petrol: number;
  alertId?: string;
}): Promise<ManualEpraCeilingResult> {
  if (!BASE) return { ok: false, error: 'The backend is not configured.' };
  try {
    const res = await authed('/api/console/epra-ceiling', 'POST', args);
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) {
      const reason = json.reason as string | undefined;
      const mapped =
        reason === 'invalid_diesel' ? 'Diesel must be between 50 and 500 KES/L.'
        : reason === 'invalid_petrol' ? 'Petrol must be between 50 and 500 KES/L.'
        : reason === 'diesel_below_petrol' ? 'Diesel cannot be cheaper than petrol on Nairobi EPRA caps.'
        : json.error || reason || 'Could not apply those ceilings.';
      return { ok: false, reason, error: mapped };
    }
    return json as ManualEpraCeilingResult;
  } catch {
    return { ok: false, error: 'Network error — please try again.' };
  }
}

/* ── Past trip crew, for the composer's "Recents" picker ───────────────────
 * A conductor's phone number lives in `tripConductors/{username}`, and
 * firestore.rules denies that collection to EVERY client (backend admin SDK
 * only) — so the dashboard cannot offer a returning conductor from Firestore
 * the way it can offer a returning outside parent from `users`. This endpoint
 * is the only route back to them.
 *
 * Server contract (index.js → trips.listPastTripCrew): GET with ?orgCode, and
 * `{ ok, crew: [{ kind, displayName, phone, username, email, busId, busNickname,
 * numberPlate, tripId, lastUsedAt }], total }`. displayName / phone / email are
 * null for a hired conductor with none — hence the string coercion below.
 *
 * ⚠️ TWO KINDS COME BACK. The endpoint reads `tripConductors` AND `tripParents`
 * and tags each row `kind: 'conductor' | 'parent'`, so a caller that wants only
 * the people it hired MUST filter — otherwise the conductor picker offers
 * outside guardians. A backend that predates `kind` only ever returned
 * conductors, so a missing tag defaults to 'conductor' rather than dropping the
 * row (2026-09-08).
 *
 * 2026-09-08: every failure path returns ROWS, not an exception. The picker is
 * a convenience on top of a form that still works when typed by hand, so a
 * backend that is not deployed yet must leave the Recents bar empty rather than
 * break trip creation.
 *
 * NEVER returns a password: credentials are show-once and each trip mints
 * fresh ones. Name and phone are what the picker fills; the login id is only
 * ever used to de-duplicate one person across several trips (2026-09-08 — see
 * the note on `username` below for why it must never be reused). */
export interface PastTripCrew {
  /** 'conductor' = a person this org hired; 'parent' = the guardian of an
   *  outside child. Untagged rows (older backend) are conductors. */
  kind: 'conductor' | 'parent';
  displayName: string;
  phone: string;
  /** The past login id. De-duplication ONLY — it is never prefilled into a new
   *  hire and is deliberately not shown in the picker. createTripConductor
   *  upserts on this key: reusing it merge-writes a new password, trip, bus and
   *  access window over that conductor's live account (2026-09-08). */
  username: string;
  email: string;
  /** Epoch ms of the most recent trip they worked; null when unknown. */
  lastUsedMs: number | null;
}

/** Millis from whatever a JSON backend calls a time: ms number, ISO string, or
 *  a serialised Firestore Timestamp (`_seconds` / `seconds`). */
function crewMillis(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const t = Date.parse(v);
    return Number.isFinite(t) ? t : null;
  }
  if (v && typeof v === 'object') {
    const o = v as { _seconds?: number; seconds?: number };
    const s = typeof o._seconds === 'number' ? o._seconds : o.seconds;
    if (typeof s === 'number' && Number.isFinite(s)) return s * 1000;
  }
  return null;
}

function asPastCrew(raw: unknown): PastTripCrew | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const str = (k: string) => (typeof r[k] === 'string' ? (r[k] as string).trim() : '');
  const username = str('username') || str('login');
  const displayName = str('displayName') || str('name');
  // The real email or nothing — deliberately NOT a fallback to `username`
  // (2026-09-08). Putting a past login id in the picker's email column, inches
  // from the Username box, invites the admin to retype it, and reusing a
  // username overwrites that conductor's live account. Hired conductors sign in
  // by username and have no email, so their column is simply blank.
  const email = str('email');
  if (!displayName && !username) return null;
  return {
    kind: str('kind') === 'parent' ? 'parent' : 'conductor',
    displayName: displayName || username,
    phone: str('phone') || str('phoneNumber'),
    username,
    email,
    lastUsedMs: crewMillis(r.lastUsedAt ?? r.updatedAt),
  };
}

export async function fetchPastTripCrew(
  orgCode: string,
): Promise<{ ok: boolean; crew: PastTripCrew[]; error?: string }> {
  if (!BASE) return { ok: false, crew: [], error: 'The backend is not configured.' };
  try {
    const res = await authed(`/api/trip/past-crew?orgCode=${encodeURIComponent(orgCode)}`, 'GET');
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { ok: false, crew: [], error: (json.error as string) || 'Could not load past crew.' };
    }
    const raw = (json as { crew?: unknown }).crew;
    // A backend older than this caller answers 200 with no `crew` — empty list,
    // not a crash.
    if (!Array.isArray(raw)) return { ok: true, crew: [] };
    const crew = raw.map(asPastCrew).filter((c): c is PastTripCrew => c !== null);
    return { ok: true, crew };
  } catch {
    return { ok: false, crew: [], error: 'Network error — please try again.' };
  }
}
